import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { handleError } from '@mssgme/lib';
import { __t } from '@mssgme/shared';
import { Spinner, Button } from '@mssgme/ui';

import { CardReviewsPaneHeader } from '../CardReviewsPaneHeader';
import { CardReviewRecommend } from '../CardReviewRecommend';
import { CardReviewsList } from '../CardReviewsList';
import { CardMoreButton } from '../../CardMoreButton';
import { CardReviewsItem } from '../CardReviewItem';
import { CardReviewMessageModal } from '../CardReviewMessageModal';

import styles from './styles.scss';

export default class CardReviewsPane extends React.Component {
    static propTypes = {
        animated: PropTypes.bool,
        reviews: PropTypes.array.isRequired,
        myReviews: PropTypes.array,
        recommends: PropTypes.array,
        fullName: PropTypes.string.isRequired,
        isFetching: PropTypes.bool.isRequired,
        hasNext: PropTypes.bool.isRequired,
        children: PropTypes.any,
        isLoaded: PropTypes.bool,
        actions: PropTypes.shape({
            loadMore: PropTypes.func,
            onDelete: PropTypes.func,
            onRecommend: PropTypes.func,
        }),
    };

    static defaultProps = {
        isLoaded: true,
        actions: {},
    };

    state = {
        confirmIsOpen: false,
        confirmCallback: null,
    };

    confirm = (message, callback) => (e) => {
        e.preventDefault();

        this.setState({
            confirmMessage: message,
            confirmIsOpen: true,
            confirmCallback: callback,
        });
    };

    onCancel = (e) => {
        e.preventDefault();

        this.setState({ confirmIsOpen: false });
    };

    onConfirm = async (e) => {
        e.preventDefault();

        try {
            await this.state.confirmCallback();
            this.setState({ confirmIsOpen: false });
        } catch (err) {
            handleError(err);
        }
    };

    render() {
        const {
            animated,
            reviews,
            myReviews,
            fullName,
            isFetching,
            hasNext,
            recommends,
            actions,
            children,
            isLoaded,
        } = this.props;
        const { confirmIsOpen, confirmMessage } = this.state;

        let content;

        if (isLoaded) {
            const ids = myReviews.map(({ _id }) => _id);
            const _reviews = reviews.reduce((list, review) => {
                if (ids.indexOf(review._id) === -1) {
                    list.push(<CardReviewsItem key={review._id} review={review} />);
                }
                return list;
            }, []);

            content = (
                <>
                    {recommends && (
                        <CardReviewsPaneHeader
                            reviewsCountAll={recommends[0] + recommends[1]}
                            reviewsCountPositive={recommends[0]}
                            reviewsCountNegative={recommends[1]}
                        />
                    )}
                    {myReviews.length === 0 && (
                        <>
                            <CardReviewRecommend onRecommend={actions.onRecommend} fullName={fullName} />
                        </>
                    )}
                    <CardReviewsList>
                        <>
                            {myReviews.map((review) => (
                                <CardReviewsItem
                                    key={review._id}
                                    review={review}
                                    onDelete={this.confirm(__t('want_delete_review'), () => actions.onDelete(review))}
                                />
                            ))}
                            {_reviews}
                        </>
                    </CardReviewsList>
                    {reviews.length > 0 && <CardReviewsList items={reviews} />}
                    {hasNext && <CardMoreButton onClick={actions.loadMore} disabled={isFetching} />}
                    {confirmIsOpen && (
                        <CardReviewMessageModal closeModalFn={this.onCancel}>
                            <h1>{confirmMessage}</h1>
                            <Button btnStyle="secondary" outline size="small" onClick={this.onCancel}>
                                {__t('cancel')}
                            </Button>
                            <Button btnStyle="danger" size="small" onClick={this.onConfirm}>
                                {__t('delete')}
                            </Button>
                        </CardReviewMessageModal>
                    )}
                    {children}
                </>
            );
        } else {
            content = <Spinner />;
        }

        return (
            <div className={classNames(styles.root, styles.slideDown, animated ? styles.animated : null)}>
                {content}
            </div>
        );
    }
}
